import { colors } from "app/popleads/components/ui-kit/colors";
import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import ProfileCard, { EditableField } from "app/popleads/components/ui-kit/ProfileCard";
import { toAbsoluteUrl } from "_metronic/_helpers";
import SVG from "react-inlinesvg";
import FeatherIcon from "feather-icons-react";
import ExtensionCard from "app/popleads/components/ui-kit/ExtensionCard";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { useExtensionContext } from "../ExtensionContext";
import InputWithIcon from "app/popleads/components/ui-kit/Input";
import { StyledFooterButton } from "./ExtensionNewTask";
import Switch from "app/popleads/components/ui-kit/Switch";
import useDeleteProfileInfo from "app/popleads/hooks/useDeleteProfileInfo";
import Modal from "app/popleads/components/ui-kit/Modal";
import { ExtensionBody } from "app/popleads/components/ui-kit/Text";
import CustomSelect from "app/popleads/components/ui-kit/Select";
import useSetProfileExportOption from "app/popleads/hooks/useSetProfileExportOption";
import { toast } from "react-toastify";
import * as yup from "yup";
import useLocationOptions from "app/popleads/hooks/useLocationOptions";
import { useLayoutEffect } from "react";

const ExtensionPopupContentBody = styled.div`
    width: 100%;
    height: 100%;
    padding: 24px;
    overflow-y: auto;
`;

const StyledLocationContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: 10px;
`;
const StyledButtonContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: 12px;
`;
const StyledText = styled("div")`
    font-size: 14px;
    font-weight: 500;
    color: ${colors.neutral[800]};
    overflow-wrap: anywhere;
`;

// const StyledContainer = styled("div")`
//     display: flex;
//     flex-direction: column;
//     gap: 24px;
// `;
const StyledAboutPageWrapper = styled("div")`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 400px;
`;

const DetailContent = styled.div`
    max-height: ${(props) => (props.isExpanded ? "500px" : "0")};
    opacity: ${(props) => (props.isExpanded ? "1" : "0")};
    transition: all 0.3s ease-in-out;
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${(props) => !props.isExpanded && "overflow: hidden;"};
`;

const StyledAboutFooter = styled(StyledFooterButton)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    display: flex;
    position: absolute;
    width: 410px;
`;

const FlexEndWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const FIELD_TYPES = {
    EMAIL: "email",
    PHONE: "tel",
};

const FIELD_NAMES = {
    ad: "Name",
    site: "Linkedin URL",
    resim: "Image",
    baslik: "Title",
    sirket: "Company",
    country: "City/Country",
    location: "Location",
};

const EMAIL_MAX_ENTRIES = 2;
const PHONE_MAX_ENTRIES = 3;

const ExtensionAboutPage = () => {
    const {
        profile,
        setProfile,
        isProfileLoading,
        updateProfileLoading,
        handleUpdateProfile,
        refetchProfile,
        selectedModal,
        modals,
        setState,
        urlParams,
        fieldToUpdate,
    } = useExtensionContext();
    const { deleteProfileInfo, loading: deleteLoading } = useDeleteProfileInfo();
    const [emails, setEmails] = useState([]);
    const [phones, setPhones] = useState([]);
    const [newItemField, setNewItemField] = useState("");
    const [newEmailValue, setNewEmailValue] = useState("");
    const [newPhoneValue, setNewPhoneValue] = useState("");
    const [itemToDelete, setItemToDelete] = useState(null);
    const [changedFields, setChangedFields] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const [noExport, setNoExport] = useState(profile?.exportOption !== 1);
    const [exportOptionLoading, setExportOptionLoading] = useState(false);
    const { setProfileExportOption } = useSetProfileExportOption();

    const emailValidationSchema = yup
        .string()
        .email("Invalid email format")
        .max(140, "Email must be at most 140 characters");
    const phoneValidationSchema = yup.string().max(140, "Phone number must be at most 140 characters");
    const titleValidationSchema = yup.string().max(150, "Title must be at most 150 characters");

    const locationRef = useRef(null);

    const [country, setCountry] = useState(
        profile?.country ? { label: profile.country, value: profile.country } : null
    );
    const [city, setCity] = useState(profile?.city ? { label: profile.city, value: profile.city } : null);

    const { countryOptions, cityOptions, isLoading, updateCityOptions } = useLocationOptions();

    const cityAndCountry = profile?.city ? `${profile?.city}, ${profile?.country}` : profile?.country;

    useEffect(() => {
        if (profile && urlParams) {
            const changes = getChangedFields();
            setChangedFields(changes);
        }
    }, [profile, urlParams, getChangedFields]);

    useEffect(() => {
        if (profile) {
            setNoExport(profile.exportOption !== 1);
        }
    }, [profile]);

    useLayoutEffect(() => {
        const handleClickOutside = (event) => {
            if (isExpanded && locationRef.current && !locationRef.current.contains(event.target)) {
                setIsExpanded(false);
                setCountry(profile?.country ? { label: profile.country, value: profile.country } : null);
                setCity(profile?.city ? { label: profile.city, value: profile.city } : null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [country, city, isExpanded]);

    const handleFieldUpdate = async (field, value) => {
        setState({
            selectedModal: modals.UPDATE_PROFILE,
            fieldToUpdate: { field, value },
        });
    };

    const handleSetProfileExportOption = async (toggledValue) => {
        const currentNoExport = noExport;
        const newNoExport = toggledValue;

        setNoExport(newNoExport);
        setExportOptionLoading(true);

        try {
            const result = await setProfileExportOption({
                profileId: profile.id,
                option: newNoExport ? "disable" : "enable",
            });

            if (result?.data?.stat === "ok") {
                toast.success("Export option was successfully updated.");
                setProfile((prevProfile) => ({
                    ...prevProfile,
                    exportOption: newNoExport ? 0 : 1,
                }));
            } else {
                toast.error(result?.error || result?.data?.text || "Failed to update export option");
                setNoExport(currentNoExport);
            }
        } catch (error) {
            toast.error("An unexpected error occurred");
            setNoExport(currentNoExport);
        } finally {
            setExportOptionLoading(false);
        }
    };

    const locationUpdate = async (value) => {
        const cityAndCountry = value.split(", ");
        const city =
            cityAndCountry.length > 1
                ? cityAndCountry
                      .slice(0, cityAndCountry.length - 1)
                      .join(", ")
                      .trim()
                : "";
        const country =
            cityAndCountry.length > 1 ? cityAndCountry[cityAndCountry.length - 1].trim() : urlParams.profil_ulke;

        let successCount = 0;
        if (country) {
            const countryResult = await handleUpdateProfile({
                field: "country",
                fieldValue: country,
                id: profile.id,
            });

            if (!countryResult?.error) {
                successCount++;
            }

            const cityResult = await handleUpdateProfile({
                field: "city",
                fieldValue: city,
                id: profile.id,
            });

            if (!cityResult?.error) {
                successCount++;
            }
        }

        if (successCount > 1) {
            const newChangedFields = { ...changedFields };
            delete newChangedFields.location;
            setChangedFields(newChangedFields);
        }
    };

    const handleConfirmUpdate = async () => {
        if (!fieldToUpdate) return;

        if (fieldToUpdate.field === "all fields") {
            const changes = Object.entries(changedFields);
            if (changes.length > 0) {
                changes.forEach(async ([field, value]) => {
                    if (field === "location") {
                        await locationUpdate(value);
                    } else {
                        const result = await handleUpdateProfile({
                            field: field,
                            fieldValue: value,
                            id: profile.id,
                        });

                        if (!result?.error) {
                            const newChangedFields = { ...changedFields };
                            delete newChangedFields[field];
                            setChangedFields(newChangedFields);
                        }
                    }
                });
            }
        } else if (fieldToUpdate.field === "location") {
            await locationUpdate(fieldToUpdate.value);
        } else {
            const result = await handleUpdateProfile({
                field: fieldToUpdate.field,
                fieldValue: fieldToUpdate.value,
                id: profile.id,
            });

            if (!result?.error) {
                const newChangedFields = { ...changedFields };
                delete newChangedFields[fieldToUpdate.field];
                setChangedFields(newChangedFields);
            }
        }

        setState({ selectedModal: null, fieldToUpdate: null });
    };

    const getChangedFields = useCallback(() => {
        if (!profile || !urlParams) return {};
        const changes = {};

        const fullName =
            profile?.ad && profile?.soyad ? `${profile?.ad} ${profile?.soyad}` : profile?.ad || profile?.soyad;
        if (urlParams.profil_ad && fullName !== urlParams.profil_ad) {
            changes.ad = urlParams.profil_ad;
        }
        if (urlParams.profil_resim && profile.resim !== urlParams.profil_resim) {
            changes.resim = urlParams.profil_resim;
        }
        if (
            urlParams.profil_email &&
            profile.email !== urlParams.profil_email &&
            profile.email2 !== urlParams.profil_email
        ) {
            changes.email = urlParams.profil_email;
        }
        if (urlParams.profil_tel && profile.tel !== urlParams.profil_tel && profile.tel !== urlParams.profil_tel) {
            changes.tel = urlParams.profil_tel;
        }
        if (urlParams.profil_baslik && profile.baslik !== urlParams.profil_baslik) {
            changes.baslik = urlParams.profil_baslik;
        }
        if (urlParams.profil_sirket && profile.sirket !== urlParams.profil_sirket) {
            changes.sirket = urlParams.profil_sirket;
        }
        if (urlParams.profil_ulke && cityAndCountry !== urlParams.profil_ulke) {
            changes.location = urlParams.profil_ulke;
        }
        return changes;
    }, [profile, urlParams]);

    const handleLocationUpdate = async () => {
        setIsExpanded(false);
        const newCountry = country?.value ? country.value : null;
        const newCity = city?.value ? city.value : null;
        if (profile?.country !== newCountry) {
            const autoRefech = profile?.city === newCity;
            handleUpdateProfile({ field: "country", fieldValue: newCountry }, autoRefech);
        }
        if (profile?.city !== newCity) {
            handleUpdateProfile({ field: "city", fieldValue: newCity });
        }
    };

    const workListArray = [
        {
            icon: <SVG src={toAbsoluteUrl("/media/svg/popleads/work-bag.svg")} />,
            content: (
                <StyledText>
                    <EditableField
                        value={profile?.sirket}
                        placeholder={"Search or create an account"}
                        field="sirket"
                        inputType="select"
                    />
                </StyledText>
            ),
        },
        urlParams?.profil_sirket &&
            profile?.sirket !== urlParams.profil_sirket && {
                content: (
                    <div>
                        <StyledLocationContainer>
                            <div>
                                <SVG src={toAbsoluteUrl("/media/svg/popleads/warning2.svg")} />
                            </div>
                            <StyledText>Changed to "{urlParams.profil_sirket}"</StyledText>
                        </StyledLocationContainer>
                        <FlexEndWrapper>
                            <ButtonWithText
                                variant="primary"
                                size="unstyled"
                                buttonStyle="borderless"
                                onClick={() => handleFieldUpdate("sirket", urlParams.profil_sirket)}
                            >
                                Apply Changes
                            </ButtonWithText>
                        </FlexEndWrapper>
                    </div>
                ),
            },
        {
            icon: <SVG src={toAbsoluteUrl("/media/svg/popleads/bag-extension.svg")} />,
            content: (
                <StyledText>
                    <EditableField
                        value={profile?.baslik}
                        placeholder={"Please enter a title"}
                        field="baslik"
                        validationSchema={titleValidationSchema}
                    />
                </StyledText>
            ),
        },
        urlParams?.profil_baslik &&
            profile?.baslik !== urlParams.profil_baslik && {
                content: (
                    <div>
                        <StyledLocationContainer>
                            <div>
                                <SVG src={toAbsoluteUrl("/media/svg/popleads/warning2.svg")} />
                            </div>
                            <StyledText>Changed to "{urlParams.profil_baslik}"</StyledText>
                        </StyledLocationContainer>
                        <FlexEndWrapper>
                            <ButtonWithText
                                variant="primary"
                                size="unstyled"
                                buttonStyle="borderless"
                                onClick={() => handleFieldUpdate("baslik", urlParams.profil_baslik)}
                            >
                                Apply Changes
                            </ButtonWithText>
                        </FlexEndWrapper>
                    </div>
                ),
            },
    ].filter(Boolean);

    const locationArray = [
        {
            icon: <SVG src={toAbsoluteUrl("/media/svg/popleads/location.svg")} />,
            content: (
                <div>
                    <StyledText>
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                if (!isExpanded && country && cityOptions.length === 0) {
                                    updateCityOptions(country);
                                }
                                setIsExpanded(!isExpanded);
                            }}
                        >
                            {profile?.city ? `${profile?.city}, ${profile?.country}` : profile?.country || ""}
                        </span>
                    </StyledText>
                    <DetailContent
                        isExpanded={isExpanded}
                        ref={locationRef}
                        // tabIndex={-1}
                        // onBlur={(e) => {
                        //     const nextFocus = e.relatedTarget;
                        //     console.log(nextFocus);
                        //     if (!nextFocus || !locationRef.current.contains(e.currentTarget)) {
                        //         // handleLocationUpdate();
                        //     }
                        // }}
                    >
                        <CustomSelect
                            key={"country-single-select"}
                            placeholder={"Select Country"}
                            isClearable={false}
                            isSearchable={true}
                            isCreatable={false}
                            isMulti={false}
                            value={country}
                            options={countryOptions}
                            disabled={isLoading}
                            onChange={(selected) => {
                                updateCityOptions(selected);
                                setCountry(selected);
                                // setCity(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleLocationUpdate();
                                } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setIsExpanded(false);
                                    setCountry(
                                        profile?.country ? { label: profile.country, value: profile.country } : null
                                    );
                                    setCity(profile?.city ? { label: profile.city, value: profile.city } : null);
                                }
                            }}
                        />
                        <CustomSelect
                            key={"city-single-select"}
                            placeholder={"Select or Add City"}
                            isClearable={true}
                            isSearchable={true}
                            isCreatable={true}
                            formatCreateLabel={(inputValue) => `Add City: ${inputValue}`}
                            isMulti={false}
                            value={city}
                            options={cityOptions}
                            onChange={(selected) => {
                                setCity(selected);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleLocationUpdate();
                                } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setIsExpanded(false);
                                    setCountry(
                                        profile?.country ? { label: profile.country, value: profile.country } : null
                                    );
                                    setCity(profile?.city ? { label: profile.city, value: profile.city } : null);
                                }
                            }}
                        />
                    </DetailContent>
                </div>
            ),
        },
        urlParams?.profil_ulke &&
            cityAndCountry !== urlParams.profil_ulke && {
                content: (
                    <div>
                        <StyledLocationContainer>
                            <div>
                                <SVG src={toAbsoluteUrl("/media/svg/popleads/warning2.svg")} />
                            </div>
                            <StyledText>Changed to "{urlParams.profil_ulke}"</StyledText>
                        </StyledLocationContainer>
                        <FlexEndWrapper>
                            <ButtonWithText
                                variant="primary"
                                size="unstyled"
                                buttonStyle="borderless"
                                onClick={() => handleFieldUpdate("location", urlParams.profil_ulke)}
                            >
                                Apply Changes
                            </ButtonWithText>
                        </FlexEndWrapper>
                    </div>
                ),
            },
    ].filter(Boolean);

    const handleRemove = useCallback(
        async (field, value) => {
            try {
                const result = await deleteProfileInfo({
                    profileId: profile.id,
                    param: field,
                    paramValue: value,
                });

                if (!result?.error) {
                    refetchProfile();
                    setState({ selectedModal: null });
                }
            } catch (error) {
                setState({ selectedModal: null });
            }
        },
        [deleteProfileInfo, profile?.id, refetchProfile, setState]
    );

    const handleDeleteClick = useCallback(
        (field, value) => {
            setItemToDelete({ field, value });
            setState({ selectedModal: modals.DELETE_CONTACT });
        },
        [setState, modals]
    );

    const handleAdd = useCallback((field) => {
        setNewItemField(field);
    }, []);

    const validateNewValue = useCallback((newValue, profile) => {
        const allValues = Object.values(profile).filter(
            (value) => value && typeof value === "string" && value.trim() !== "" && value !== "undefined"
        );

        if (allValues.includes(newValue.trim())) {
            toast.error("This value already exists!");
            return false;
        }
        return true;
    }, []);

    const getNextEmptyField = useCallback((existingFields, fieldType) => {
        if (existingFields.length === 0) {
            return fieldType;
        } else if (fieldType === "email" && existingFields.length < EMAIL_MAX_ENTRIES) {
            for (let i = 1; i <= EMAIL_MAX_ENTRIES; i++) {
                const index = i > 1 ? i : "";
                if (!existingFields.includes(`${fieldType}${index}`)) {
                    return `${fieldType}${index}`;
                }
            }
        } else if (fieldType === "tel" && existingFields.length < PHONE_MAX_ENTRIES) {
            for (let i = 1; i <= PHONE_MAX_ENTRIES; i++) {
                const index = i > 1 ? i : "";
                if (!existingFields.includes(`${fieldType}${index}`)) {
                    return `${fieldType}${index}`;
                }
            }
        }
        toast.error(
            fieldType === "email"
                ? "A maximum of 2 email entries is allowed."
                : "A maximum of 3 phone number entries is allowed."
        );
        return null;
    }, []);

    const handleInputKeyPress = useCallback(
        async (e) => {
            if (e.key === "Enter" || e.type === "blur") {
                if (newItemField === FIELD_TYPES.EMAIL && !newEmailValue.trim()) return;
                if (newItemField === FIELD_TYPES.PHONE && !newPhoneValue.trim()) return;

                const isInvalidValue =
                    newItemField === FIELD_TYPES.EMAIL
                        ? !validateNewValue(newEmailValue, profile)
                        : !validateNewValue(newPhoneValue, profile);
                if (isInvalidValue) return;

                try {
                    if (newItemField === FIELD_TYPES.EMAIL) {
                        await emailValidationSchema.validate(newEmailValue);
                    }
                    if (newItemField === FIELD_TYPES.PHONE) {
                        await phoneValidationSchema.validate(newPhoneValue);
                    }
                } catch (err) {
                    toast.error(err.message);
                    return;
                }

                const existingFields = Object.entries(profile)
                    .filter(
                        ([key, value]) =>
                            key.includes(newItemField) && value && typeof value === "string" && value.trim() !== ""
                    )
                    .map(([key]) => key);

                const emptyField = getNextEmptyField(existingFields, newItemField);
                if (!emptyField) return;

                try {
                    await handleUpdateProfile({
                        field: emptyField,
                        fieldValue: newItemField === FIELD_TYPES.EMAIL ? newEmailValue.trim() : newPhoneValue.trim(),
                        id: profile.id,
                    });

                    setNewItemField("");
                    setNewEmailValue("");
                    setNewPhoneValue("");
                    refetchProfile();
                } catch (error) {
                    //add error toast
                }
            }
        },
        [
            newItemField,
            newEmailValue,
            newPhoneValue,
            profile,
            handleUpdateProfile,
            refetchProfile,
            getNextEmptyField,
            validateNewValue,
        ]
    );

    const handleCancelAdd = useCallback(() => {
        setNewItemField("");
        setNewEmailValue("");
        setNewPhoneValue("");
    }, []);

    const createChangeNotification = (fieldKey, urlParams, profile, handleFieldUpdate) => {
        const fieldMap = {
            email: {
                paramKey: "profil_email",
                profileKey: "email",
            },
            tel: {
                paramKey: "profil_tel",
                profileKey: "tel",
            },
        };

        const { paramKey, profileKey } = fieldMap[fieldKey] || {};
        const newValue = urlParams?.[paramKey];
        const oldValue = profile?.[profileKey];

        if (newValue && oldValue !== newValue) {
            return {
                content: (
                    <div>
                        <StyledLocationContainer>
                            <div>
                                <SVG src={toAbsoluteUrl("/media/svg/popleads/warning2.svg")} />
                            </div>
                            <StyledText>Changed to "{newValue}"</StyledText>
                        </StyledLocationContainer>
                        <FlexEndWrapper>
                            <ButtonWithText
                                variant="primary"
                                size="unstyled"
                                buttonStyle="borderless"
                                onClick={() => handleFieldUpdate(fieldKey, newValue)}
                            >
                                Apply Changes
                            </ButtonWithText>
                        </FlexEndWrapper>
                    </div>
                ),
            };
        }

        return null;
    };

    const generateContent = useCallback(
        (profile, field, hasRightContent = false) => {
            if (!profile || !field) return [];

            const icon = field === FIELD_TYPES.EMAIL ? "mail" : "phone";
            const regex = new RegExp(`^${field}\\d?$`);
            let arrayOfContent = [];

            const items = Object.entries(profile).filter(
                ([key, value]) =>
                    key.includes(field) &&
                    regex.test(key) &&
                    value &&
                    typeof value === "string" &&
                    value.trim() !== "" &&
                    value !== "undefined"
            );

            const fieldKey = field === FIELD_TYPES.EMAIL ? "email" : "tel";

            if (items.length === 0 && newItemField !== field) {
                arrayOfContent.push({
                    icon: <FeatherIcon strokeWidth="1px" icon={icon} size="16" />,
                    content: (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <StyledText>
                                <InputWithIcon
                                    variant="unstyled"
                                    value=""
                                    onChange={() => {}}
                                    placeholder={field === FIELD_TYPES.EMAIL ? "Add Email" : "Add Phone"}
                                    onClick={() => handleAdd(field)}
                                    onFocus={() => handleAdd(field)}
                                    style={{ cursor: "pointer" }}
                                />
                            </StyledText>
                            <StyledButtonContainer>
                                <ButtonWithText
                                    icon="add"
                                    iconType="material"
                                    variant="primary"
                                    size="circle"
                                    buttonStyle="default"
                                    onClick={() => handleAdd(field)}
                                />
                            </StyledButtonContainer>
                        </div>
                    ),
                });
                if (!profile[fieldKey]) {
                    const notification = createChangeNotification(fieldKey, urlParams, profile, handleFieldUpdate);
                    if (notification) {
                        arrayOfContent.push(notification);
                    }
                }
                return arrayOfContent;
            }

            items.forEach(([key, value], index) => {
                arrayOfContent.push({
                    icon: <FeatherIcon strokeWidth="1px" icon={icon} size="16" />,
                    content: (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <StyledText style={{ width: "100%" }}>
                                <EditableField
                                    placeholder={
                                        field === FIELD_TYPES.EMAIL
                                            ? "Please enter an email"
                                            : "Please Enter a phone number"
                                    }
                                    validationSchema={
                                        field === FIELD_TYPES.EMAIL ? emailValidationSchema : phoneValidationSchema
                                    }
                                    value={value}
                                    field={key}
                                />
                            </StyledText>
                            {hasRightContent && (
                                <StyledButtonContainer>
                                    <ButtonWithText
                                        icon="remove"
                                        iconType="material"
                                        variant="danger"
                                        size="circle"
                                        buttonStyle="inverted"
                                        onClick={() => handleDeleteClick(key, value)}
                                    />
                                    {items.length - 1 === index &&
                                        ((field === "email" && items.length < EMAIL_MAX_ENTRIES) ||
                                            (field === "tel" && items.length < PHONE_MAX_ENTRIES)) && (
                                            <ButtonWithText
                                                icon="add"
                                                iconType="material"
                                                variant="primary"
                                                size="circle"
                                                buttonStyle="default"
                                                onClick={() => handleAdd(field)}
                                            />
                                        )}
                                </StyledButtonContainer>
                            )}
                        </div>
                    ),
                });
                const notification = createChangeNotification(key, urlParams, profile, handleFieldUpdate);
                if (notification) {
                    arrayOfContent.push(notification);
                }
            });

            if (newItemField === field) {
                arrayOfContent.push({
                    icon: <FeatherIcon strokeWidth="1px" icon={icon} size="16" />,
                    content: (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                            tabIndex={-1}
                            onBlur={(e) => {
                                const nextFocus = e.relatedTarget;
                                if (!nextFocus || !e.currentTarget.contains(nextFocus)) {
                                    // handleInputKeyPress(e);
                                    if (field === FIELD_TYPES.EMAIL) {
                                        setNewEmailValue("");
                                    } else {
                                        setNewPhoneValue("");
                                    }
                                }
                            }}
                        >
                            <StyledText style={{ width: "100%" }}>
                                <InputWithIcon
                                    key={field}
                                    debounceTime={0}
                                    variant="unstyled"
                                    value={field === FIELD_TYPES.EMAIL ? newEmailValue : newPhoneValue}
                                    onChange={(e) => {
                                        if (field === FIELD_TYPES.EMAIL) {
                                            setNewEmailValue(e.target.value);
                                        } else {
                                            setNewPhoneValue(e.target.value);
                                        }
                                    }}
                                    onKeyPress={handleInputKeyPress}
                                    autoFocus
                                    placeholder={field === FIELD_TYPES.EMAIL ? "Add Email" : "Add Phone"}
                                />
                            </StyledText>
                            <StyledButtonContainer>
                                <ButtonWithText
                                    icon="remove"
                                    iconType="material"
                                    variant="danger"
                                    size="circle"
                                    buttonStyle="inverted"
                                    onClick={handleCancelAdd}
                                />
                            </StyledButtonContainer>
                        </div>
                    ),
                });
                if (!profile[fieldKey]) {
                    const notification = createChangeNotification(fieldKey, urlParams, profile, handleFieldUpdate);
                    if (notification) {
                        arrayOfContent.push(notification);
                    }
                }
            }

            return arrayOfContent;
        },
        [newItemField, newEmailValue, newPhoneValue, handleAdd, handleDeleteClick, handleCancelAdd, handleInputKeyPress]
    );

    useEffect(() => {
        if (!profile) return;

        const validProfile = Object.fromEntries(
            Object.entries(profile).filter(
                ([_, value]) => value && typeof value === "string" && value.trim() !== "" && value !== "undefined"
            )
        );

        const emailContent = generateContent(validProfile, FIELD_TYPES.EMAIL, true);
        const phoneContent = generateContent(validProfile, FIELD_TYPES.PHONE, true);

        if (JSON.stringify(emails) !== JSON.stringify(emailContent)) {
            setEmails(emailContent);
        }
        if (JSON.stringify(phones) !== JSON.stringify(phoneContent)) {
            setPhones(phoneContent);
        }

        setCountry(profile?.country ? { label: profile.country, value: profile.country } : null);
        setCity(profile?.city ? { label: profile.city, value: profile.city } : null);
    }, [profile, generateContent, emails, phones]);

    if (isProfileLoading || updateProfileLoading || deleteLoading) {
        return (
            <ExtensionPopupContentBody>
                <SpinnerContainer>
                    <Spinner animation="border" variant="primary" />
                </SpinnerContainer>
            </ExtensionPopupContentBody>
        );
    }

    return (
        <>
            <ExtensionPopupContentBody>
                <StyledAboutPageWrapper>
                    <ProfileCard variant="neutralInActive" />
                    <ExtensionCard cardTitle="Contact" haveViewMore={false} cardItems={[...emails, ...phones]} />
                    <ExtensionCard cardTitle="Location" cardItems={locationArray} />
                    <ExtensionCard haveViewMore={false} cardTitle="Work" cardItems={workListArray} />
                </StyledAboutPageWrapper>
            </ExtensionPopupContentBody>
            <StyledAboutFooter>
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <ExtensionBody>No-Export</ExtensionBody>
                    <Switch
                        isActive={noExport}
                        onClick={() => handleSetProfileExportOption(!noExport)}
                        loading={exportOptionLoading}
                    />
                </div>
                {Object.entries(changedFields).length > 1 && (
                    <div className="d-flex justify-content-end">
                        <ButtonWithText
                            variant="primary"
                            size="unstyled"
                            buttonStyle="borderless"
                            onClick={() => handleFieldUpdate("all fields", "all")}
                        >
                            <span style={{ fontSize: "14px" }}>Apply All Changes</span>
                        </ButtonWithText>
                    </div>
                )}
            </StyledAboutFooter>
            <Modal
                isOpen={selectedModal === modals.DELETE_CONTACT}
                onClose={() => setState({ selectedModal: null })}
                title={
                    itemToDelete?.field?.includes("email")
                        ? "Are you sure you want to delete this email?"
                        : "Are you sure you want to delete this phone number?"
                }
                variant="danger"
                icon="delete"
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onSuccess={() => itemToDelete && handleRemove(itemToDelete.field, itemToDelete.value)}
            >
                <div>This action cannot be undone.</div>
            </Modal>
            <Modal
                isOpen={selectedModal === modals.UPDATE_PROFILE}
                onClose={() => {
                    setState({ selectedModal: null });
                }}
                title="Update Profile Field"
                variant="primary"
                icon="edit"
                successButtonText="Yes, Update"
                cancelButtonText="Not Now"
                onSuccess={handleConfirmUpdate}
            >
                {fieldToUpdate?.field === "all fields" ? (
                    <div>Do you want to update all fields?</div>
                ) : (
                    <div>
                        Do you want to update the {FIELD_NAMES[fieldToUpdate?.field] || fieldToUpdate?.field} to "
                        {fieldToUpdate?.value}"?
                    </div>
                )}
            </Modal>
        </>
    );
};

export default ExtensionAboutPage;
